import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, ChevronDown, ChevronUp,
  Package, Heart, MapPin, Phone, Mail, Calendar, Loader2, Download, Upload, Filter,
} from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { adminApi } from '@/lib/api';
import { exportToExcel, importFromExcel } from '@/lib/export-utils';
import toast from 'react-hot-toast';
import type { User, OrderStatus } from '@/types';

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600', confirmed: 'bg-blue-500/10 text-blue-600',
  processing: 'bg-purple-500/10 text-purple-600', shipped: 'bg-indigo-500/10 text-indigo-600',
  delivered: 'bg-green-500/10 text-green-600', cancelled: 'bg-red-500/10 text-red-600',
};
const STATUS_DOTS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-500', confirmed: 'bg-blue-500', processing: 'bg-purple-500',
  shipped: 'bg-indigo-500', delivered: 'bg-green-500', cancelled: 'bg-red-500',
};

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const orders = useAdminStore((s) => s.orders);
  const artworks = useAdminStore((s) => s.artworks);

  useEffect(() => {
    adminApi.getUsers().then((data) => {
      setUsers(data as User[]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [exportFilter, setExportFilter] = useState<'all' | 'with-orders' | 'no-orders' | 'with-wishlist'>('all');
  const [showExportOptions, setShowExportOptions] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let items = [...users].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (q) {
      items = items.filter((u) =>
        u.firstName.toLowerCase().includes(q) ||
        u.lastName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    }
    return items;
  }, [users, search]);

  const getOrderCount = (userId: string): number =>
    orders.filter((o) => o.userId === userId).length;

  const getOrderTotal = (userId: string): number =>
    orders.filter((o) => o.userId === userId).reduce((sum, o) => sum + o.total, 0);

  const formatDate = (date: string): string =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatCurrency = (amount: number, currency?: string): string => {
    if (currency === 'GEL') return (amount / 100).toLocaleString() + ' ₾';
    return '$' + (amount / 100).toLocaleString();
  };

  const getExportUsers = (): User[] => {
    let list = [...filtered];
    if (exportFilter === 'with-orders') list = list.filter((u) => getOrderCount(u._id) > 0);
    else if (exportFilter === 'no-orders') list = list.filter((u) => getOrderCount(u._id) === 0);
    else if (exportFilter === 'with-wishlist') list = list.filter((u) => u.wishlist.length > 0);
    return list;
  };

  const handleExportUsers = (): void => {
    const exportList = getExportUsers();
    if (!exportList.length) { toast.error('No users to export with current filter'); return; }
    const data = exportList.map((u) => ({
      'Name': `${u.firstName} ${u.lastName}`,
      'Email': u.email,
      'Phone': u.phone || '',
      'Birth Date': u.birthDate || '',
      'Address': u.address ? [u.address.address, u.address.city, u.address.country].filter(Boolean).join(', ') : '',
      'Orders': getOrderCount(u._id),
      'Total Spent': '$' + (getOrderTotal(u._id) / 100).toFixed(2),
      'Wishlist Items': u.wishlist?.length || 0,
      'Registered': formatDate(u.createdAt),
    }));
    exportToExcel(data, { filename: `users-${exportFilter}-${new Date().toISOString().slice(0, 10)}`, sheetName: 'Users' });
    toast.success(`Exported ${exportList.length} users`);
    setShowExportOptions(false);
  };

  const importFileRef = React.useRef<HTMLInputElement>(null);
  const handleImportUsers = async (file: File): Promise<void> => {
    try {
      const rows = await importFromExcel(file);
      toast.success(`Imported ${rows.length} rows — review in console`);
      console.log('Imported user data:', rows);
    } catch { toast.error('Failed to import file'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Users</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {users.length} registered user{users.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 relative">
          <button onClick={() => setShowExportOptions(!showExportOptions)} className="flex items-center gap-2 px-4 py-2.5 bg-black/[0.05] hover:bg-black/[0.1] text-[10px] font-bold uppercase tracking-wider rounded-xl transition-all">
            <Download size={14} /> Export <ChevronDown size={12} />
          </button>
          <AnimatePresence>
            {showExportOptions && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-[#111] border border-black/10 dark:border-white/10 rounded-xl shadow-xl z-50 p-2">
                {([['all', 'All Users'], ['with-orders', 'With Orders'], ['no-orders', 'Without Orders'], ['with-wishlist', 'With Wishlist']] as const).map(([key, label]) => (
                  <button key={key} onClick={() => setExportFilter(key)} className={`w-full text-left px-3 py-2 text-[11px] font-medium rounded-lg transition-all ${exportFilter === key ? 'bg-black dark:bg-white text-white dark:text-black' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}>
                    {label} ({key === 'all' ? filtered.length : key === 'with-orders' ? filtered.filter((u) => getOrderCount(u._id) > 0).length : key === 'no-orders' ? filtered.filter((u) => getOrderCount(u._id) === 0).length : filtered.filter((u) => u.wishlist.length > 0).length})
                  </button>
                ))}
                <button onClick={handleExportUsers} className="w-full mt-2 py-2.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-wider rounded-lg">
                  Export {getExportUsers().length} Users
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          <button onClick={() => importFileRef.current?.click()} className="flex items-center gap-2 px-4 py-2.5 bg-black/[0.05] hover:bg-black/[0.1] text-[10px] font-bold uppercase tracking-wider rounded-xl transition-all">
            <Upload size={14} /> Import
          </button>
          <input ref={importFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportUsers(f); e.target.value = ''; }} />
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
          placeholder="Search by name or email..."
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-black/20" />
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((user) => {
            const orderCount = getOrderCount(user._id);
            const orderTotal = getOrderTotal(user._id);
            const isExpanded = expandedId === user._id;
            const userOrders = orders.filter((o) => o.userId === user._id);
            const wishlistArtworks = artworks.filter((aw) => user.wishlist.includes(aw._id));

            return (
              <motion.div
                key={user._id}
                layout
                className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden"
              >
                {/* Summary row */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : user._id)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center text-sm font-bold font-syncopate shrink-0">
                    {user.firstName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold truncate">{user.firstName} {user.lastName}</p>
                    <p className="text-[10px] text-black/40 dark:text-white/40 truncate">{user.email}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-6 text-[10px] text-black/40 dark:text-white/40 shrink-0">
                    <span className="flex items-center gap-1"><Package size={12} /> {orderCount}</span>
                    <span className="flex items-center gap-1"><Heart size={12} /> {user.wishlist.length}</span>
                    <span>{formatCurrency(orderTotal)}</span>
                    <span className="text-[9px]">{formatDate(user.createdAt)}</span>
                  </div>
                  {isExpanded ? <ChevronUp size={16} className="text-black/20 dark:text-white/20 shrink-0" /> : <ChevronDown size={16} className="text-black/20 dark:text-white/20 shrink-0" />}
                </button>

                {/* Expanded details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-5 pt-1 space-y-4 border-t border-black/[0.04] dark:border-white/[0.04]">
                        {/* Contact info */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="flex items-center gap-2 text-[11px] text-black/60 dark:text-white/60">
                            <Mail size={13} className="text-black/30 dark:text-white/30" />
                            {user.email}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-black/60 dark:text-white/60">
                            <Phone size={13} className="text-black/30 dark:text-white/30" />
                            {user.phone || 'No phone'}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-black/60 dark:text-white/60">
                            <Calendar size={13} className="text-black/30 dark:text-white/30" />
                            Joined {formatDate(user.createdAt)}
                          </div>
                        </div>

                        {/* Address */}
                        {user.address && (user.address.address || user.address.city) && (
                          <div className="flex items-start gap-2 text-[11px] text-black/60 dark:text-white/60">
                            <MapPin size={13} className="text-black/30 dark:text-white/30 mt-0.5 shrink-0" />
                            <span>
                              {[user.address.address, user.address.city, user.address.country, user.address.postalCode]
                                .filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}

                        {/* Orders */}
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-2">
                            Orders ({orderCount})
                          </p>
                          {userOrders.length > 0 ? (
                            <div className="space-y-1.5">
                              {userOrders.slice(0, 5).map((order) => (
                                <button
                                  key={order._id}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/admin/orders/${order._id}`); }}
                                  className="w-full flex items-center justify-between py-2 px-3 bg-black/[0.02] dark:bg-white/[0.02] rounded-lg hover:bg-black/[0.05] dark:hover:bg-white/[0.05] transition-colors group"
                                >
                                  <span className="text-[10px] font-mono text-black/40 dark:text-white/40 group-hover:text-black dark:group-hover:text-white transition-colors">{order.orderNumber || `#${order._id.slice(0, 8)}`}</span>
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider ${STATUS_COLORS[order.status]}`}>
                                    <span className={`w-1 h-1 rounded-full ${STATUS_DOTS[order.status]}`} />
                                    {order.status}
                                  </span>
                                  <span className="text-[10px] text-black/50 dark:text-white/50">{order.items.length} item{order.items.length !== 1 ? 's' : ''}</span>
                                  <span className="text-[10px] font-bold">{formatCurrency(order.total, order.currency)}</span>
                                  <span className="text-[9px] text-black/30 dark:text-white/30">{formatDate(order.createdAt)}</span>
                                </button>
                              ))}
                              {userOrders.length > 5 && (
                                <p className="text-[9px] text-black/30 dark:text-white/30 text-center mt-1">+{userOrders.length - 5} more orders</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-[10px] text-black/20 dark:text-white/20">No orders yet</p>
                          )}
                        </div>

                        {/* Wishlist */}
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-2">
                            Wishlist ({user.wishlist.length})
                          </p>
                          {wishlistArtworks.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {wishlistArtworks.slice(0, 8).map((aw) => (
                                <div key={aw._id} className="flex items-center gap-2 px-2 py-1.5 bg-black/[0.02] dark:bg-white/[0.02] rounded-lg">
                                  <img src={aw.images?.[0] || ''} alt="" className="w-6 h-6 rounded object-cover" />
                                  <span className="text-[10px] font-medium truncate max-w-[120px]">{aw.title.en}</span>
                                </div>
                              ))}
                              {user.wishlist.length > 8 && (
                                <span className="text-[10px] text-black/30 dark:text-white/30 self-center">+{user.wishlist.length - 8} more</span>
                              )}
                            </div>
                          ) : (
                            <p className="text-[10px] text-black/20 dark:text-white/20">Empty wishlist</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
            <Users size={24} className="text-black/10 dark:text-white/10" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            {users.length === 0 ? 'No registered users yet' : 'No users match your search'}
          </p>
          {users.length === 0 && (
            <p className="text-[10px] text-black/15 dark:text-white/15 mt-1">
              Users will appear here when customers register
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
